import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, QrCode } from 'lucide-react';
import { api, ApiError } from '@/api';
import SocialButtons from '@/components/SocialButtons';
import { JOB_TITLE_OPTIONS, JOB_TITLE_OTHER, jobTitleSelection } from '@shared/ui/job-title.js';
import RegistrationSuccess from '@/components/RegistrationSuccess';

const FIELDS = [
  { key: 'full_name', labelKey: 'register.fullName', required: true, type: 'text', autoComplete: 'name' },
  { key: 'email', labelKey: 'register.email', required: true, type: 'email', autoComplete: 'email' },
  { key: 'company', labelKey: 'register.company', required: false, type: 'text', autoComplete: 'organization' },
  { key: 'job_title', labelKey: 'register.jobTitle', required: false, type: 'select' },
  { key: 'phone', labelKey: 'register.phone', required: false, type: 'tel', autoComplete: 'tel' },
];

/**
 * Registration.
 *
 * Three entry paths converge on one participant record: the form, and either
 * social provider. `pendingSignup` is the state after an OAuth round trip for
 * someone we have never seen — we hold their profile and ask for the missing
 * details plus consent, because clicking "Continue with Google" is not consent
 * to process their data for this event.
 */
export default function RegisterForm({
  open = true,
  event,
  providers = [],
  pendingSignup = null,
  existingTicket = null,
  onCompleted,
}) {
  const { t, i18n } = useTranslation();

  const isSocialSignup = Boolean(pendingSignup);
  const [form, setForm] = useState({
    full_name: pendingSignup?.profile?.full_name || '',
    email: pendingSignup?.profile?.email || '',
    company: '',
    job_title: '',
    phone: '',
  });

  /*
   * The select and its «Άλλο» text are UI state; `form.job_title` stays the one
   * stored value. Seeded from whatever is already there so a social sign-up
   * that arrives with a job title, or an existing free-text value, still shows.
   */
  const seeded = jobTitleSelection(form.job_title);
  const [jobChoice, setJobChoice] = useState(seeded.choice);
  const [jobOther, setJobOther] = useState(seeded.other);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [ticket, setTicket] = useState(null);
  const anchorRef = useRef(null);

  /*
   * Fill in what the provider told us, once it arrives.
   *
   * The OAuth exchange is a round trip that resolves *after* this component has
   * mounted, so seeding these through useState's initial value read
   * `pendingSignup` while it was still null — and a useState initialiser never
   * runs again. The name and email fields are also read-only on this path, so
   * the two together produced a form that showed nothing and could not be
   * typed into.
   */
  useEffect(() => {
    if (!pendingSignup?.profile) return;
    setForm((prev) => ({
      ...prev,
      full_name: pendingSignup.profile.full_name || prev.full_name,
      email: pendingSignup.profile.email || prev.email,
    }));
  }, [pendingSignup]);

  /** Someone who signed in with a provider they had already used. */
  useEffect(() => {
    if (existingTicket?.ticket_code) setTicket(existingTicket);
  }, [existingTicket]);

  useEffect(() => {
    if (ticket || isSocialSignup) anchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [ticket, isSocialSignup]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!consent) {
      setError(t('register.consentRequired'));
      return;
    }

    setBusy(true);
    try {
      let created;
      if (isSocialSignup) {
        const result = await api.request('/auth/oauth/complete', {
          method: 'POST',
          auth: false,
          body: {
            ticket: pendingSignup.ticket,
            full_name: form.full_name,
            company: form.company,
            job_title: form.job_title,
            phone: form.phone,
            locale: i18n.resolvedLanguage,
            consent: true,
          },
        });
        created = { email: form.email, ticket_code: result.ticket_code };
        onCompleted?.(result);
      } else {
        created = await api.participants.register({
          ...form,
          email: form.email.trim().toLowerCase(),
          source: 'microsite',
          locale: i18n.resolvedLanguage,
          consent: true,
        });
      }
      setTicket(created);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'email_taken') setError(t('register.emailTaken'));
      else if (err instanceof ApiError && err.code === 'registration_closed') setError(t('microsite.closed'));
      else if (err instanceof ApiError && err.code === 'consent_required') setError(t('register.consentRequired'));
      else if (err instanceof ApiError && err.code === 'invalid_ticket') setError(t('social.expired'));
      else setError(t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  if (!open && !ticket) {
    return (
      <div className="bg-card border border-white/10 rounded-2xl p-8 text-center">
        <p className="font-heading font-bold uppercase text-white/50 text-xl">{t('microsite.closed')}</p>
      </div>
    );
  }

  if (ticket) {
    return (
      <div ref={anchorRef}>
        {/* Signing out drops the session and returns to an empty form, which is
            what the next person on a shared machine needs to see. */}
        <RegistrationSuccess ticket={ticket} event={event} onSignOut={() => setTicket(null)} />
      </div>
    );
  }

  // Name and email come from the provider and are shown read-only: editing them
  // would let someone register under an address they have not proven they own.
  const fields = isSocialSignup
    ? FIELDS.map((f) => ({ ...f, readOnly: f.key === 'email' || f.key === 'full_name' }))
    : FIELDS;

  return (
    <div ref={anchorRef}>
      {isSocialSignup && (
        <div className="mb-4">
          <h3 className="font-heading font-black uppercase text-white text-2xl">{t('social.completeTitle')}</h3>
          <p className="text-white/50 text-sm font-body mt-1">{t('social.completeSubtitle')}</p>
        </div>
      )}

      <form onSubmit={submit} className="bg-card border border-white/10 rounded-2xl p-6 sm:p-8 space-y-3">
        {!isSocialSignup && <SocialButtons providers={providers} returnTo="/#register" />}

        {fields.map((f) => (
          <div key={f.key}>
            <label htmlFor={`reg-${f.key}`} className="label-tag text-white/40 text-[10px] block mb-1.5">
              {t(f.labelKey)}
              {f.required && ' *'}
            </label>
            {f.type === 'select' ? (
              <>
                <select
                  id={`reg-${f.key}`}
                  required={f.required}
                  value={jobChoice}
                  onChange={(e) => {
                    const choice = e.target.value;
                    setJobChoice(choice);
                    // The sentinel is never stored: «Άλλο» stores what they type.
                    setForm((prev) => ({
                      ...prev,
                      job_title: choice === JOB_TITLE_OTHER ? jobOther : choice,
                    }));
                  }}
                  className="w-full px-4 py-3 rounded-xl border text-sm font-body focus:outline-none bg-white/5 border-white/10 text-white focus:border-brand/50 focus:ring-1 focus:ring-brand/30"
                >
                  <option value="">{t('register.jobTitleSelect')}</option>
                  {JOB_TITLE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {t(o.labelKey)}
                    </option>
                  ))}
                </select>
                {jobChoice === JOB_TITLE_OTHER && (
                  <input
                    type="text"
                    aria-label={t('register.jobTitleOtherLabel')}
                    placeholder={t('register.jobTitleOtherLabel')}
                    value={jobOther}
                    onChange={(e) => {
                      setJobOther(e.target.value);
                      setForm((prev) => ({ ...prev, job_title: e.target.value }));
                    }}
                    className="w-full px-4 py-3 rounded-xl border text-sm font-body focus:outline-none bg-white/5 border-white/10 text-white focus:border-brand/50 focus:ring-1 focus:ring-brand/30 mt-2"
                  />
                )}
              </>
            ) : (
            <input
              id={`reg-${f.key}`}
              type={f.type}
              autoComplete={f.autoComplete}
              required={f.required}
              readOnly={f.readOnly}
              value={form[f.key]}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              className={`w-full px-4 py-3 rounded-xl border text-sm font-body focus:outline-none
                          placeholder:text-white/20 ${
                            f.readOnly
                              ? 'bg-white/[0.02] border-white/5 text-white/50 cursor-not-allowed'
                              : 'bg-white/5 border-white/10 text-white focus:border-brand/50 focus:ring-1 focus:ring-brand/30'
                          }`}
            />
            )}
          </div>
        ))}

        <label className="flex gap-3 items-start pt-3 cursor-pointer">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5 w-4 h-4 shrink-0 accent-[#abffc1]"
          />
          <span className="text-white/50 text-xs leading-relaxed font-body">
            {t('register.consent')}{' '}
            <Link to="/privacy" className="text-brand underline underline-offset-2">
              {t('microsite.privacyLink')}
            </Link>
          </span>
        </label>

        {error && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-xl bg-destructive/10 border border-destructive/30 px-3 py-2.5"
          >
            <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
            <p className="text-destructive text-xs font-body">{error}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={busy || !consent}
          className="w-full mt-2 py-4 rounded-xl bg-brand text-black font-heading font-black uppercase text-xl
                     disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2
                     hover:brightness-95 transition"
        >
          {busy ? (
            <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <QrCode className="w-5 h-5" /> {t('register.submit')}
            </>
          )}
        </button>
      </form>
    </div>
  );
}
